import { AiInsights_AiInsightListItem_Type as AiInsightListItem_Type } from '@zendesk/zqa-services/autoqa'
import { AutoQaSpotlightType } from '@/types/spotlight'
import { AutoQaCategoryType } from '@/types/category'

import { AutoValidationError } from '@/modules/settings/components/ai/types'
import type { DeconstructedOption } from '@/modules/conversations/utils/filters'
import i18n from '@/i18n'

export const systemSpotlightMap = {
  [AiInsightListItem_Type.DEAD_AIR]: AutoQaSpotlightType.DeadAir,
  [AiInsightListItem_Type.RECORDING_DISCLOSURE]: AutoQaSpotlightType.RecordingDisclosure,
  [AiInsightListItem_Type.CHURN_RISK]: AutoQaSpotlightType.ChurnRisk,
  [AiInsightListItem_Type.EXTRA_MILE]: AutoQaSpotlightType.ExtraMile,
  [AiInsightListItem_Type.SENTIMENT]: AutoQaSpotlightType.Sentiment,
  [AiInsightListItem_Type.OUTLIER]: AutoQaSpotlightType.Outlier,
  [AiInsightListItem_Type.FOLLOW_UP]: AutoQaSpotlightType.FollowUp,
  [AiInsightListItem_Type.ESCALATION_REQUEST]: AutoQaSpotlightType.EscalationRequest,
  [AiInsightListItem_Type.SLA]: AutoQaSpotlightType.SLA,
  [AiInsightListItem_Type.BOT_REPETITION]: AutoQaSpotlightType.BotRepetition,
  [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: AutoQaSpotlightType.BotCommunicationEfficiency,
}

export const systemCategoryMap = {
  [AiInsightListItem_Type.COMPREHENSION]: AutoQaCategoryType.Comprehension,
}

export const includesHandledValidationError = (error: string) => {
  const errorsArray = error.split(';').map((error) => error.trim())
  const enumValues: string[] = Object.values(AutoValidationError)
  return errorsArray.some((error) => enumValues.includes(error.toLowerCase()))
}

export const mapFilterOptions = (options: DeconstructedOption[]) =>
  options.map(({ option, condition }) => ({
    option: option.name,
    condition: condition.name,
  }))

enum FeDefinedAiInsightListItem_Type {
  ISSUE_UNDERSTANDING = 'ISSUE_UNDERSTANDING',
}

const spotlightTranslationKeys = {
  name: {
    [AiInsightListItem_Type.DEAD_AIR]: 'settings.spotlight.deadair.title',
    [AiInsightListItem_Type.RECORDING_DISCLOSURE]: 'settings.spotlight.recording_disclosure.title',
    [AiInsightListItem_Type.CHURN_RISK]: 'prompt_library.prompt.spotlight.name.churn_risk',
    [AiInsightListItem_Type.EXTRA_MILE]: 'settings.spotlight.extra_mile.title',
    [AiInsightListItem_Type.SENTIMENT]: 'settings.spotlight.sentiment.title',
    [AiInsightListItem_Type.OUTLIER]: 'settings.spotlight.outlier.title',
    [AiInsightListItem_Type.FOLLOW_UP]: 'settings.spotlight.follow_up.title',
    [AiInsightListItem_Type.ESCALATION_REQUEST]: 'settings.spotlight.escalation_request.title',
    [AiInsightListItem_Type.SLA]: 'settings.spotlight.sla.title',
    [AiInsightListItem_Type.BOT_REPETITION]: 'filter.option.label.bot_repetition',
    [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: 'filter.option.label.bot_communication_efficiency',
  },
  description: {
    [AiInsightListItem_Type.DEAD_AIR]: 'settings.ai.spotlights.dead_air.description',
    [AiInsightListItem_Type.RECORDING_DISCLOSURE]: 'settings.ai.spotlights.recording_disclosure.description',
    [AiInsightListItem_Type.CHURN_RISK]: 'prompt_library.prompt.spotlight.description.churn_risk',
    [AiInsightListItem_Type.EXTRA_MILE]: 'settings.spotlights.extra_mile.description',
    [AiInsightListItem_Type.SENTIMENT]: 'settings.spotlights.sentiment.description',
    [AiInsightListItem_Type.OUTLIER]: 'settings.spotlights.outlier.description',
    [AiInsightListItem_Type.FOLLOW_UP]: 'settings.spotlights.follow_up.description',
    [AiInsightListItem_Type.ESCALATION_REQUEST]: 'settings.spotlights.escalation_request.description',
    [AiInsightListItem_Type.SLA]: 'settings.spotlights.sla.description',
    [AiInsightListItem_Type.BOT_REPETITION]: 'filter.option.description.bot_repetition',
    [AiInsightListItem_Type.BOT_COMMUNICATION_EFFICIENCY]: 'filter.option.description.bot_communication_efficiency',
  },
}

const categoryTranslationKeys = {
  name: {
    [AiInsightListItem_Type.COMPREHENSION]:
      'settings.workspaces.rating_categories.edit.auto_categories.issue_understanding.name',
    [AiInsightListItem_Type.EMPATHY]: 'settings.workspaces.rating_categories.edit.auto_categories.empathy.name',
    [AiInsightListItem_Type.GREETING]: 'settings.workspaces.rating_categories.edit.auto_categories.greeting.name',
    [AiInsightListItem_Type.CLOSING]: 'settings.workspaces.rating_categories.edit.auto_categories.closing.name',
    [AiInsightListItem_Type.GRAMMAR]: 'settings.workspaces.rating_categories.edit.auto_categories.grammar.name',
    [AiInsightListItem_Type.READABILITY]: 'settings.workspaces.rating_categories.edit.auto_categories.readability.name',
    [AiInsightListItem_Type.TONE]: 'settings.workspaces.rating_categories.edit.auto_categories.tone.name',
    [AiInsightListItem_Type.SOLUTION]: 'settings.workspaces.rating_categories.edit.auto_categories.solution.name',
    [FeDefinedAiInsightListItem_Type.ISSUE_UNDERSTANDING]:
      'settings.workspaces.rating_categories.edit.auto_categories.issue_understanding.name',
  },
  description: {
    [AiInsightListItem_Type.COMPREHENSION]: 'settings.ai.categories.issue_understanding.description',
    [AiInsightListItem_Type.EMPATHY]: 'settings.ai.categories.empathy.description',
    [AiInsightListItem_Type.GREETING]: 'settings.ai.categories.greeting.description',
    [AiInsightListItem_Type.CLOSING]: 'settings.ai.categories.closing.description',
    [AiInsightListItem_Type.GRAMMAR]: 'settings.ai.categories.grammar.description',
    [AiInsightListItem_Type.READABILITY]: 'settings.ai.categories.readability.description',
    [AiInsightListItem_Type.TONE]: 'settings.ai.categories.tone.description',
    [AiInsightListItem_Type.SOLUTION]: 'settings.ai.categories.solution.description',
    [FeDefinedAiInsightListItem_Type.ISSUE_UNDERSTANDING]: 'settings.ai.categories.issue_understanding.description',
  },
}

export function translateSystemCategoryName(code: string) {
  if (!code) return ''
  const key = categoryTranslationKeys.name[code.toUpperCase()]
  return key ? i18n.t(key) : ''
}

export function translateSystemCategoryDescription(code: string) {
  if (!code) return ''
  const key = categoryTranslationKeys.description[code.toUpperCase()]
  return key ? i18n.t(key) : ''
}

export function translateSystemSpotlightName(code: string) {
  if (!code) return ''
  const key = spotlightTranslationKeys.name[code.toUpperCase()]
  return key ? i18n.t(key) : ''
}

export function translateSystemSpotlightDescription(code: string) {
  if (!code) return ''
  const key = spotlightTranslationKeys.description[code.toUpperCase()]
  return key ? i18n.t(key) : ''
}

export function isSystemAutoQACategory(type: AutoQaCategoryType) {
  return type && type !== AutoQaCategoryType.Custom && type !== AutoQaCategoryType.Prompt
}
